import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { formatDate, formatDateTime } from "@core/utils";
import { daysUntil, type PublicRoundDetail } from "../../types";

/**
 * The Aperçu's header — « Bandeau appel » (Figma `521:28540`).
 *
 * Two variants, and the difference is not decoration. **Open** carries a warm gradient wash and
 * invites; **closed** goes flat grey and states that the dossier is now only readable. The frame's
 * own note: the round identity and the deadline are READ FROM THE FROZEN CONFIG, never typed here.
 *
 * The lock line is the visual form of US-E11-01's core rule — *"reading the dossier must not create
 * any record of the person reading it"*. It stays on screen rather than living in a code comment,
 * because the person it reassures is the applicant, not us.
 */
export function PreviewHeader({
  round,
  serverNow,
}: {
  round: PublicRoundDetail;
  /** The server clock from the same payload. Never `Date.now()` — a visitor's device can be days out. */
  serverNow: string;
}) {
  const { t, i18n } = useTranslation("acquire");

  const isOpen = round.accepting_applications;
  const days = daysUntil(round.closing_at, serverNow);

  /**
   * The deadline line.
   *
   * A round that says its dates are NOT definitive gets the softer phrasing — printing a
   * provisional deadline in the same voice as a fixed one is how a page ends up more certain than
   * the round it describes.
   */
  const closingLine = (() => {
    if (!isOpen) {
      return t("preview.header.closed-on", {
        date: formatDate(round.closing_at, i18n.language) ?? "",
      });
    }
    const at = formatDateTime(round.closing_at, i18n.language) ?? "";
    const base = round.dates_are_definitive
      ? t("preview.header.closes-at", { datetime: at })
      : t("preview.header.closes-at-provisional", { datetime: at });
    // The countdown is additive, and silent on the last day rather than printing "in 0 days".
    return days !== null && days > 0
      ? `${base} · ${t("preview.header.in-days", { count: days })}`
      : base;
  })();

  return (
    <header
      className={
        isOpen
          ? // The design's wash is a vertical brand→white gradient. Kept as a token-derived tint
            // rather than the frame's raw hex, which the source itself flags as a known deviation.
            "flex flex-col gap-md bg-linear-to-b from-brand-50 to-surface-card px-md py-xl sm:px-xl"
          : "flex flex-col gap-md bg-surface-page px-md py-xl sm:px-xl"
      }
    >
      {/* Status pill. Its own row so it never competes with the title for width at 320px. */}
      <span
        className={
          isOpen
            ? "inline-flex w-fit items-center gap-xs rounded-full border border-(--status-open-border) bg-surface-card px-sm py-0.5 text-xs-medium text-(--status-open-text)"
            : "inline-flex w-fit items-center gap-xs rounded-full border border-(--border-default) bg-surface-card px-sm py-0.5 text-xs-medium text-(--text-secondary)"
        }
      >
        <span
          aria-hidden
          className={
            isOpen
              ? "size-2 rounded-full bg-(--status-open-dot)"
              : "size-2 rounded-full bg-(--status-closed-dot)"
          }
        />
        {isOpen
          ? t("preview.header.pill-open")
          : t("preview.header.pill-closed")}
      </span>

      <div className="flex flex-col gap-xxs">
        <h1 className="display-xs-semibold text-(--text-primary)">
          {t("preview.header.title")}
        </h1>
        <p className="text-sm-regular text-(--text-secondary)">
          {isOpen
            ? t("preview.header.subtitle-open")
            : t("preview.header.subtitle-closed")}
        </p>
      </div>

      {/* The round's own identity — frozen config, never hand-written copy. */}
      <div className="flex flex-col gap-0.5">
        <p className="text-sm-semibold text-(--text-primary)">{round.name}</p>
        <p className="text-sm-regular text-(--text-secondary)">{closingLine}</p>
        {isOpen ? null : (
          <p className="text-sm-regular text-(--text-secondary)">
            {t("preview.header.next-round-announced")}
          </p>
        )}
      </div>

      {/*
        US-E11-01's rule, said out loud. Not a footnote: the account wall is the largest suspected
        source of abandonment, and "this costs you nothing to read" is the whole reason the page
        exists.
      */}
      <p className="flex items-start gap-xs text-xs-regular text-(--text-tertiary)">
        <span className="mt-0.5 shrink-0">
          <Icon name="lock" size={16} />
        </span>
        {t("preview.header.no-trace")}
      </p>
    </header>
  );
}
